// PersonaLogin — three visually distinct food-tech style login pages.
// /customer  → warm, appetite-forward (Zomato / Swiggy energy)
// /chef      → confident, kitchen-forward (Uber-Eats-for-chefs vibe)
// /admin     → dark command-centre (mission-control aesthetic)
// The OTP form on the right is IDENTICAL across all three so the existing
// data-testids and integrations keep working.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BrandMark } from "@/components/Brand";
import {
  ChefHat, Utensils, MapPin, Star, Timer, Flame, Sparkles,
  ShieldCheck, Activity, Users, Bell, BadgeCheck, IndianRupee, ArrowUpRight,
} from "lucide-react";

const DEST_BY_ROLE = { admin: "/admin", chef: "/chef", customer: "/app" };

// ------------------------------------------------------------------
// Persona-scoped copy + palette
// ------------------------------------------------------------------
const PERSONAS = {
  customer: {
    title: "Sign in — Customer",
    tagline: "Home food, cooked in your kitchen — every single day.",
    placeholder: "10-digit mobile",
    heroBg: "bg-[#fff5ec]",
    heroAccent: "text-[#ff6b35]",
    formTitleBrow: "Get started",
    formPitch: "Your chef, your kitchen, your kind of home food.",
  },
  chef: {
    title: "Sign in — Chef",
    tagline: "Cook where you love, earn what you deserve.",
    placeholder: "Your registered mobile",
    heroBg: "bg-[#0f2b1f]",
    heroAccent: "text-[#f5c518]",
    formTitleBrow: "Chef sign-in",
    formPitch: "Private URL — please do not share.",
  },
  admin: {
    title: "Sign in — Admin",
    tagline: "The nerve centre of Delco Chefs.",
    placeholder: "Registered admin mobile",
    heroBg: "bg-[#0b0d17]",
    heroAccent: "text-[#7dd3fc]",
    formTitleBrow: "Restricted access",
    formPitch: "Only registered admin numbers can sign in here.",
  },
};

// ------------------------------------------------------------------
// Small primitives
// ------------------------------------------------------------------
function Chip({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${className}`}>
      {children}
    </span>
  );
}

// ------------------------------------------------------------------
// CUSTOMER hero — Zomato / Swiggy style bento
// ------------------------------------------------------------------
function CustomerHero() {
  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* warm sun-glow */}
      <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-[#ffb072] to-[#ff6b35] opacity-30 blur-3xl" />
      <div className="absolute bottom-0 -right-24 w-[360px] h-[360px] rounded-full bg-gradient-to-tr from-[#ffd066] to-[#ff6b35] opacity-25 blur-3xl" />
      {/* speckle grain */}
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage:"radial-gradient(#000 1px, transparent 1px)", backgroundSize:"16px 16px" }} />

      <div className="relative h-full flex flex-col justify-between p-10 lg:p-14">
        <div>
          <Chip className="bg-[#ff6b35]/12 text-[#ff6b35] border border-[#ff6b35]/25">
            <Flame size={12}/> Home meals · Noida
          </Chip>
          <h2 className="mt-6 font-bold tracking-[-0.03em] leading-[0.95] text-[#1d1d1f]" style={{ fontSize: "clamp(40px, 5vw, 64px)" }}>
            Ghar ka khaana,<br/>
            <span className="italic" style={{ background:"linear-gradient(135deg, #ff6b35, #ff2d55)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              on autopilot.
            </span>
          </h2>
          <p className="mt-4 text-base text-[#1d1d1f]/70 max-w-md leading-snug">
            A verified home chef, in your kitchen, every day. Track them live. Skip the guesswork.
          </p>
        </div>

        {/* Bento preview cards */}
        <div className="mt-8 grid grid-cols-6 gap-3 max-w-lg">
          <div className="col-span-4 rounded-3xl bg-white p-4 shadow-sm border border-black/5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#ff6b35] to-[#ff2d55] grid place-items-center text-white shadow-md">
                <ChefHat size={20}/>
              </div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-[#1d1d1f]/50 font-semibold">Tonight · 7:30 PM</div>
                <div className="text-sm font-semibold text-[#1d1d1f] truncate">Palak paneer · Jeera rice · Roti</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-[#1d1d1f]/60">
              <Chip className="bg-emerald-100 text-emerald-700"><MapPin size={10}/> 800 m away</Chip>
              <Chip className="bg-[#fff2ea] text-[#ff6b35]"><Timer size={10}/> 12 min</Chip>
            </div>
          </div>

          <div className="col-span-2 rounded-3xl bg-[#1d1d1f] p-4 text-white shadow-sm">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Chef</div>
            <div className="mt-1 text-base font-semibold">Ravi K.</div>
            <div className="mt-2 flex items-center gap-1 text-[#f5c518]">
              <Star size={12} fill="currentColor" strokeWidth={0}/>
              <Star size={12} fill="currentColor" strokeWidth={0}/>
              <Star size={12} fill="currentColor" strokeWidth={0}/>
              <Star size={12} fill="currentColor" strokeWidth={0}/>
              <Star size={12} fill="currentColor" strokeWidth={0}/>
            </div>
            <div className="mt-3 text-[10px] text-white/50">4.9 · 218 meals</div>
          </div>

          <div className="col-span-3 rounded-3xl p-4 shadow-sm border border-black/5" style={{ background:"linear-gradient(135deg, #fff, #ffefe1)" }}>
            <div className="flex items-center gap-2 text-[11px] font-semibold text-[#ff6b35]">
              <Sparkles size={12}/> This week
            </div>
            <div className="mt-2 text-xl font-bold text-[#1d1d1f]">21 meals</div>
            <div className="text-[11px] text-[#1d1d1f]/50">breakfast · lunch · dinner</div>
          </div>

          <div className="col-span-3 rounded-3xl bg-[#1d1d1f]/[0.04] p-4 border border-black/5">
            <div className="text-[11px] font-semibold text-[#1d1d1f]/60">Live tracking</div>
            <div className="mt-2 flex items-center gap-2">
              <div className="relative w-2.5 h-2.5">
                <div className="absolute inset-0 rounded-full bg-emerald-500"/>
                <div className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-70"/>
              </div>
              <span className="text-sm font-medium text-[#1d1d1f]">Ravi is 10 min away</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// CHEF hero — professional kitchen vibe
// ------------------------------------------------------------------
function ChefHero() {
  return (
    <div className="relative w-full h-full overflow-hidden text-white">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f2b1f] via-[#0f2b1f] to-[#1a3d2b]"/>
      <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-[#f5c518] opacity-10 blur-3xl"/>
      <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-emerald-400 opacity-10 blur-3xl"/>
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage:"radial-gradient(#fff 1px, transparent 1px)", backgroundSize:"18px 18px" }} />

      <div className="relative h-full flex flex-col justify-between p-10 lg:p-14">
        <div>
          <Chip className="bg-[#f5c518]/15 text-[#f5c518] border border-[#f5c518]/25">
            <ChefHat size={12}/> Chef partner portal
          </Chip>
          <h2 className="mt-6 font-bold tracking-[-0.03em] leading-[0.95]" style={{ fontSize: "clamp(40px, 5vw, 64px)" }}>
            Cook where<br/>
            you love, earn<br/>
            <span style={{ background:"linear-gradient(135deg, #f5c518, #ff9f43)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              what you deserve.
            </span>
          </h2>
          <p className="mt-4 text-base text-white/70 max-w-md leading-snug">
            One tap to see today's kitchens, meals, and payouts. Delco takes care of the rest.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-6 gap-3 max-w-lg">
          <div className="col-span-4 rounded-3xl bg-white/[0.06] backdrop-blur-xl p-4 border border-white/10">
            <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">Today · Wed</div>
            <div className="mt-2 space-y-1.5">
              <div className="flex items-center gap-2 text-sm">
                <span className="w-9 text-[11px] tabular-nums text-white/60">07:30</span>
                <span className="flex-1 h-6 rounded-md bg-[#f5c518]/25 border border-[#f5c518]/40 grid place-items-center text-[11px] text-[#f5c518] font-semibold">Breakfast · Meena household</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="w-9 text-[11px] tabular-nums text-white/60">12:30</span>
                <span className="flex-1 h-6 rounded-md bg-emerald-400/25 border border-emerald-400/40 grid place-items-center text-[11px] text-emerald-300 font-semibold">Lunch · Kapoor household</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="w-9 text-[11px] tabular-nums text-white/60">19:30</span>
                <span className="flex-1 h-6 rounded-md bg-[#ff9f43]/25 border border-[#ff9f43]/40 grid place-items-center text-[11px] text-[#ff9f43] font-semibold">Dinner · Sharma household</span>
              </div>
            </div>
          </div>

          <div className="col-span-2 rounded-3xl bg-[#f5c518] text-[#0f2b1f] p-4 shadow-lg">
            <div className="text-[10px] uppercase tracking-widest opacity-60 font-semibold">This week</div>
            <div className="mt-1 flex items-baseline gap-1">
              <IndianRupee size={16} className="translate-y-[2px]"/>
              <span className="text-2xl font-bold tabular-nums">12,400</span>
            </div>
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold">
              <ArrowUpRight size={10}/> 18% vs last
            </div>
          </div>

          <div className="col-span-3 rounded-3xl bg-white/[0.06] backdrop-blur-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 text-[11px] text-white/60">
              <BadgeCheck size={12} className="text-emerald-400"/> Verified chef
            </div>
            <div className="mt-2 text-sm">Ravi K. · 4.9 ★</div>
            <div className="text-[11px] text-white/50">238 slots completed</div>
          </div>

          <div className="col-span-3 rounded-3xl bg-white/[0.06] backdrop-blur-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 text-[11px] text-white/60">
              <Utensils size={12}/> Specialities
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              <Chip className="bg-white/10 text-white">Punjabi</Chip>
              <Chip className="bg-white/10 text-white">South Indian</Chip>
              <Chip className="bg-white/10 text-white">Jain</Chip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// ADMIN hero — dark command centre
// ------------------------------------------------------------------
function AdminHero() {
  return (
    <div className="relative w-full h-full overflow-hidden text-white">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0b0d17] via-[#0b0d17] to-[#1a1f3a]"/>
      <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-[#7dd3fc] opacity-[0.08] blur-3xl"/>
      <div className="absolute bottom-0 -right-32 w-[420px] h-[420px] rounded-full bg-[#a78bfa] opacity-[0.10] blur-3xl"/>
      {/* dot grid */}
      <div className="absolute inset-0 opacity-[0.10]" style={{ backgroundImage:"radial-gradient(#7dd3fc 1px, transparent 1px)", backgroundSize:"24px 24px" }} />

      <div className="relative h-full flex flex-col justify-between p-10 lg:p-14">
        <div>
          <Chip className="bg-white/8 text-[#7dd3fc] border border-white/10">
            <ShieldCheck size={12}/> Restricted · admin only
          </Chip>
          <h2 className="mt-6 font-bold tracking-[-0.03em] leading-[0.95]" style={{ fontSize: "clamp(40px, 5vw, 64px)" }}>
            Command<br/>
            <span style={{ background:"linear-gradient(135deg, #7dd3fc, #a78bfa)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              centre.
            </span>
          </h2>
          <p className="mt-4 text-base text-white/60 max-w-md leading-snug">
            Every chef, every kitchen, every meal — one dashboard. Sign in with your registered mobile.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-6 gap-3 max-w-lg">
          <div className="col-span-3 rounded-3xl bg-white/[0.04] backdrop-blur-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 text-[11px] text-white/60"><Activity size={12} className="text-emerald-400"/> Chefs live</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums">12</span>
              <span className="text-[11px] text-emerald-300">/ 14 on shift</span>
            </div>
            {/* mini activity bars */}
            <div className="mt-3 flex items-end gap-1 h-6">
              {[40, 60, 30, 80, 55, 90, 45, 70, 50, 65].map((h, i) => (
                <div key={i} className="flex-1 rounded-t bg-[#7dd3fc]/60" style={{ height: `${h}%` }}/>
              ))}
            </div>
          </div>

          <div className="col-span-3 rounded-3xl bg-white/[0.04] backdrop-blur-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 text-[11px] text-white/60"><Users size={12} className="text-[#a78bfa]"/> Active mappings</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums">86</span>
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
                <ArrowUpRight size={10}/> 4 this week
              </span>
            </div>
            <div className="mt-3 flex -space-x-2">
              {["#7dd3fc","#a78bfa","#f472b6","#fbbf24","#34d399"].map((c, i) => (
                <div key={i} className="w-6 h-6 rounded-full border-2 border-[#0b0d17]" style={{ background: c }}/>
              ))}
              <div className="w-6 h-6 rounded-full grid place-items-center text-[9px] font-semibold text-white bg-white/10 border-2 border-[#0b0d17]">+81</div>
            </div>
          </div>

          <div className="col-span-4 rounded-3xl bg-white/[0.04] backdrop-blur-xl p-4 border border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] text-white/60"><MapPin size={12} className="text-[#7dd3fc]"/> Noida coverage</div>
              <Chip className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">98% uptime</Chip>
            </div>
            {/* stylised map dots */}
            <div className="relative mt-3 h-16 rounded-xl bg-[#7dd3fc]/[0.04] border border-white/5 overflow-hidden">
              {[["18%","30%"],["30%","55%"],["45%","20%"],["58%","70%"],["72%","40%"],["86%","55%"]].map(([l, t], i) => (
                <div key={i} className="absolute w-2 h-2 rounded-full bg-[#7dd3fc]" style={{ left: l, top: t }}>
                  <div className="absolute inset-0 rounded-full bg-[#7dd3fc] animate-ping opacity-60"/>
                </div>
              ))}
              <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 100 40" preserveAspectRatio="none">
                <path d="M0,30 Q25,10 50,25 T100,15" stroke="#7dd3fc" strokeWidth="0.4" fill="none" strokeDasharray="1 1"/>
              </svg>
            </div>
          </div>

          <div className="col-span-2 rounded-3xl bg-white/[0.04] backdrop-blur-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 text-[11px] text-white/60"><Bell size={12} className="text-[#fbbf24]"/> Alerts</div>
            <div className="mt-1 text-3xl font-bold tabular-nums">3</div>
            <div className="text-[11px] text-white/50">2 open · 1 SLA</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const HEROES = { customer: CustomerHero, chef: ChefHero, admin: AdminHero };

// ------------------------------------------------------------------
// Main
// ------------------------------------------------------------------
export default function PersonaLogin({ intent = "customer" }) {
  const { user, sendOtp, verifyOtp, resendOtp } = useAuth();
  const nav = useNavigate();
  const p = useMemo(() => PERSONAS[intent] || PERSONAS.customer, [intent]);
  const Hero = HEROES[intent] || CustomerHero;

  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [returning, setReturning] = useState(false);

  useEffect(() => {
    if (!user) return;
    nav(DEST_BY_ROLE[user.role] || "/", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const submitPhone = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    // 1) Pre-check — is this mobile already in users? Reuses backend normalize_msisdn.
    let existing = null;
    try {
      const { data } = await api.post("/auth/check-mobile", { mobile: phone.trim() });
      existing = data;
    } catch (err) {
      // If the pre-check itself fails (network/400), we fall through to send-otp which
      // will re-validate the mobile format and return the correct error.
      existing = { exists: false, role: null };
    }
    // 2) Persona-scoped guard: admin URL rejects unknown numbers with a friendly message
    //    (backend also enforces this — belt & braces).
    if (intent === "admin" && !existing.exists) {
      setBusy(false);
      setError("This number is not registered as an admin. Contact support@delcochefs.com for access.");
      return;
    }
    // 3) Flip UI copy: if number belongs to the SAME persona we're on, greet as returning
    //    user. If it belongs to another persona, backend will route them to the correct
    //    dashboard on verify anyway — but we still say "logging you in" because there is
    //    no signup for them.
    setReturning(!!existing.exists);
    if (existing.exists) toast.info("You're already registered — logging you in");

    // 4) Fire the OTP send (same endpoint whether signup or login — MSG91 unchanged).
    const res = await sendOtp(phone.trim(), intent);
    setBusy(false);
    if (!res.ok) { setError(res.error || "Could not send OTP"); return; }
    setStep("otp"); setCooldown(30);
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const res = await verifyOtp({ mobile: phone.trim(), otp: otp.trim(), intent });
    setBusy(false);
    if (!res.ok) { setError(res.error || "Invalid or expired OTP"); return; }
    nav(DEST_BY_ROLE[res.role] || DEST_BY_ROLE[res.user?.role] || "/", { replace: true });
  };

  const doResend = async () => {
    if (cooldown > 0) return;
    setBusy(true);
    const res = await resendOtp(phone.trim());
    setBusy(false);
    if (!res.ok) { toast.error(res.error || "Could not resend OTP"); return; }
    setCooldown(30);
    toast.success("New OTP sent");
  };

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row overflow-x-hidden" data-testid={`persona-login-${intent}`}>
      {/* HERO — full width on mobile too so the persona identity carries through */}
      <div className={`relative overflow-hidden ${p.heroBg} lg:w-[56%] min-h-[280px] lg:min-h-screen`}>
        <Hero/>
      </div>

      {/* FORM */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center gap-3">
            <BrandMark size={40}/>
            <span className={`text-xs uppercase tracking-[0.25em] font-semibold ${p.heroAccent}`}>
              {returning ? "Welcome back" : p.formTitleBrow}
            </span>
            <h1 className="heading text-2xl text-[#1d1d1f]" data-testid="persona-login-title">
              {returning ? `Sign in — ${intent.charAt(0).toUpperCase() + intent.slice(1)}` : p.title}
            </h1>
            <p className="text-sm text-muted-foreground text-center max-w-xs">
              {returning ? "Enter the OTP we just sent to your registered mobile." : p.formPitch}
            </p>
          </div>

          {step === "phone" ? (
            <form onSubmit={submitPhone} className="space-y-4" data-testid="phone-form">
              <div>
                <Label htmlFor="phone">Mobile number</Label>
                <div className="mt-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground select-none tabular-nums">+91</span>
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    autoFocus
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    name="delco-mobile"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g,"").slice(0,10))}
                    placeholder={p.placeholder}
                    data-testid="phone-input"
                    data-lpignore="true"
                    data-form-type="other"
                    className="pl-10 h-12 rounded-2xl"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-destructive" data-testid="phone-error">{error}</p>}
              <Button
                type="submit"
                disabled={busy || phone.length < 10}
                className="w-full rounded-full h-12 bg-[#1d1d1f] hover:bg-[#1d1d1f]/90 text-white font-medium"
                data-testid="send-otp-btn"
              >
                {busy ? "Sending…" : "Send OTP"}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                By continuing you agree to Delco Chefs' terms & privacy.
              </p>
            </form>
          ) : (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="otp-form">
              <div className="text-sm text-muted-foreground">
                We sent a code to <b className="text-[#1d1d1f]">+91 {phone.replace(/\D/g,"").slice(-10)}</b>.
                <button type="button" className="ml-2 underline hover:text-primary"
                  onClick={() => { setStep("phone"); setOtp(""); setError(""); }}
                  data-testid="edit-phone-link">
                  Edit number
                </button>
              </div>
              <div>
                <Label htmlFor="otp">6-digit OTP</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g,""))}
                  placeholder="••••••"
                  data-testid="otp-input"
                  className="mt-1 h-14 rounded-2xl tracking-[0.5em] text-center text-xl font-semibold"
                />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="otp-error">{error}</p>}
              <Button
                type="submit"
                disabled={busy || otp.length !== 6}
                className="w-full rounded-full h-12 bg-[#1d1d1f] hover:bg-[#1d1d1f]/90 text-white font-medium"
                data-testid="verify-otp-btn"
              >
                {busy ? "Verifying…" : "Verify & continue"}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={doResend}
                  disabled={cooldown > 0 || busy}
                  className="text-sm text-primary disabled:text-muted-foreground disabled:no-underline hover:underline"
                  data-testid="resend-otp-btn"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
